/**
 * ClientSiteVisits — shown on the Client Portal.
 * Lists all scheduled/completed site visits for the client's project +
 * a "Request a Visit" button that files a request into the studio inbox.
 */
import { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Buildings, CalendarBlank, Camera } from "@phosphor-icons/react";

export default function ClientSiteVisits({ projectName }) {
  const [visits, setVisits] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/site-visits?project=${encodeURIComponent(projectName)}`);
      setVisits(data || []);
    } catch (err) {
      // silent
    }
  }, [projectName]);

  useEffect(() => { load(); }, [load]);

  const upcoming = visits.filter((v) => v.status === "upcoming");
  const past = visits.filter((v) => v.status !== "upcoming");

  return (
    <div data-testid="client-site-visits">
      <div className="flex items-baseline justify-between">
        <div className="label-overline text-blue-600 flex items-center gap-1.5">
          <Buildings size={12} weight="bold" /> Site Visits
        </div>
        <Button
          size="sm" variant="outline"
          onClick={() => setOpen(true)}
          data-testid="client-site-visit-request-btn"
          className="rounded-none border-blue-500 text-blue-700 font-mono uppercase tracking-widest text-[10px] px-3"
        >
          Request a Visit
        </Button>
      </div>

      {visits.length === 0 ? (
        <div className="text-sm text-zinc-500 mt-3">
          No site visits scheduled yet. Click <em>Request a Visit</em> above to ask the studio.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {upcoming.length > 0 && (
            <div>
              <div className="label-overline text-emerald-700 mb-1">Upcoming</div>
              {upcoming.map((v) => <ClientVisitRow key={v.id} v={v} />)}
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div className="label-overline text-zinc-500 mb-1">Past visits</div>
              {past.map((v) => <ClientVisitRow key={v.id} v={v} />)}
            </div>
          )}
        </div>
      )}

      {open && (
        <RequestDialog
          projectName={projectName}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ClientVisitRow({ v }) {
  return (
    <div className="border border-zinc-300 bg-white p-3 space-y-2" data-testid={`client-visit-${v.id}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarBlank size={14} weight="bold" className="text-blue-600 shrink-0" />
          <span className="font-semibold truncate">
            {new Date(v.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} IST
          </span>
        </div>
        <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
          v.status === "done" ? "border-emerald-500 text-emerald-700"
            : v.status === "cancelled" ? "border-zinc-400 text-zinc-500"
            : "border-amber-500 text-amber-700"
        }`}>{v.status}</span>
      </div>
      {v.attendee_names?.length > 0 && (
        <div className="text-xs text-zinc-600">Studio: {v.attendee_names.join(", ")}</div>
      )}
      {v.discussion && (
        <div className="text-sm whitespace-pre-wrap text-zinc-700 border-l-2 border-blue-300 pl-2">{v.discussion}</div>
      )}
      {v.next_actions && (
        <div className="text-xs text-zinc-600 border-l-2 border-emerald-300 pl-2">
          <span className="font-semibold uppercase text-[10px] tracking-widest">Next: </span>{v.next_actions}
        </div>
      )}
      {v.photos?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
            <Camera size={10} weight="bold" /> Photos ({v.photos.length})
          </div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {v.photos.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block border border-zinc-300">
                <img src={p.url} alt={p.caption || ""} className="w-full aspect-[4/3] object-cover" />
                {p.caption && <div className="text-[10px] text-center px-1 py-0.5 text-zinc-600 truncate">{p.caption}</div>}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RequestDialog({ projectName, onClose }) {
  const [preferredDate, setPreferredDate] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Please write a short note"); return; }
    setBusy(true);
    try {
      await http.post(`/projects/${encodeURIComponent(projectName)}/site-visit-request`, {
        preferred_date: preferredDate,
        message: message.trim(),
      });
      toast.success("Request sent to the studio — we'll confirm shortly");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-md"
                     data-testid="client-sv-request-dialog">
        <DialogHeader>
          <div className="label-overline text-blue-600 mb-1">Site visit</div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            Request a Visit
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-3">
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">Preferred date (optional)</div>
            <Input type="date" value={preferredDate}
                   onChange={(e) => setPreferredDate(e.target.value)}
                   data-testid="client-sv-date"
                   className="rounded-none border-zinc-400" />
          </label>
          <label className="block">
            <div className="label-overline text-zinc-500 mb-1">What would you like to see or discuss? *</div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                      required minLength={3} maxLength={2000}
                      data-testid="client-sv-msg"
                      className="w-full rounded-none border border-zinc-400 p-3 text-sm min-h-[120px]" />
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-300">
            <Button type="button" variant="outline" onClick={onClose}
                    className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button type="submit" disabled={busy}
                    data-testid="client-sv-submit"
                    className="rounded-none bg-blue-700 hover:bg-blue-800 text-white font-mono uppercase tracking-widest text-xs px-5">
              {busy ? "Sending…" : "Send Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
